/** FULFILMENT & DELIVERY — permanent section of the accepted quotation (Quotation = execution, Command Centre = oversight). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { DeliveryGrid } from "./DeliveryGrid";
import { HistoricalImport } from "./HistoricalImport";
import { FulfilmentEvents } from "./FulfilmentEvents";
import { RegisterBar } from "./RegisterBar";

const err = (e) => e.response?.data?.detail || e.message;
export const STATE_TONE = { NOT_YET_ACTIVATED: "bg-slate-200 text-slate-700", ACTIVE: "bg-sky-600 text-white", COMPLETED: "bg-emerald-600 text-white" };

export function FulfilmentStateChip({ state }) {
  return <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wider ${STATE_TONE[state] || STATE_TONE.ACTIVE}`} data-testid="fd-state" data-state={state}>{(state || "").replace(/_/g, " ")}</span>;
}

function ExperienceStrip({ s }) {
  const cell = (k, l, v) => <div key={k} className="rounded-lg bg-white/80 px-2.5 py-1.5" data-testid={`fd-xp-${k}`}><div className="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">{l}</div><div className="font-mono text-[15px] font-bold text-slate-900">{v}</div></div>;
  const n = s.recipients;
  return (
    <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-6" data-testid="fd-experience">
      {cell("recipients", "Recipients", n)}{cell("delivered", "Delivered", s.delivered)}{cell("delivery-comm", "Delivery communication", `${s.delivery_communication}/${n}`)}
      {cell("fb-requested", "Feedback requested", `${s.feedback_requested}/${n}`)}{cell("fb-received", "Feedback received", `${s.feedback_received}/${n}`)}{cell("support", "Support requests", s.support_open ? `${s.support_requests} (${s.support_open} open)` : s.support_requests)}
    </div>
  );
}

export function FulfilmentDelivery({ orderId, onChanged }) {
  const [g, setG] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => api.get(`/quote-fulfilment/orders/${orderId}/grid`).then((r) => { setG(r.data); setTick((t) => t + 1); }).catch((e) => toast.error(err(e))), [orderId]);
  useEffect(() => { load(); }, [load]);
  if (!g) return <div className="rounded-2xl border-2 border-sky-700 p-3 text-[12px] text-slate-500" data-testid="fd-loading">Loading Fulfilment &amp; Delivery…</div>;
  return (
    <section className="rounded-2xl border-2 border-sky-700 bg-sky-50/40 p-3" data-testid="fulfilment-delivery">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-[12px] font-extrabold uppercase tracking-[0.2em] text-sky-900">Fulfilment &amp; Delivery</h3>
        <FulfilmentStateChip state={g.state} />
        <span className="text-[11px] text-slate-600">Recipient → address → serial → DC → invoice → courier → AWB → welcome label → dispatch → delivery → feedback → support</span>
        <button onClick={() => setImportOpen(true)} className="ml-auto rounded-md border border-sky-700 bg-white px-2.5 py-1 text-[11px] font-semibold text-sky-800 hover:bg-sky-50" data-testid="fd-add-historical">Add / import shipments</button>
      </div>
      {g.customer_opted_out && <div className="mb-2 rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid="fd-opted-out">This customer opted out of welcome labels / experience communication.</div>}
      {g.quantities.allocated > g.quantities.ordered && <div className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid="fd-over-allocated">Allocated {g.quantities.allocated} units against {g.quantities.ordered} ordered — cancel any placeholder recipient / shipment that the delivered (historical) rows replace.</div>}
      <ExperienceStrip s={g.summary} />
      <RegisterBar orderId={orderId} g={g} reload={load} />
      <DeliveryGrid g={g} reload={load} orderId={orderId} />
      <FulfilmentEvents orderId={orderId} tick={tick} />
      <HistoricalImport open={importOpen} onOpenChange={setImportOpen} orderId={orderId} onDone={() => { load(); onChanged && onChanged(); }} />
    </section>
  );
}

export function FulfilmentNotActivated() {
  return (
    <section className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-3" data-testid="fulfilment-delivery-pending">
      <div className="flex flex-wrap items-center gap-2"><h3 className="text-[12px] font-extrabold uppercase tracking-[0.2em] text-slate-700">Fulfilment &amp; Delivery</h3><FulfilmentStateChip state="NOT_YET_ACTIVATED" /></div>
      <p className="mt-1 text-[12px] text-slate-600">Activates automatically in this quotation once it is accepted — recipients, address verification, serials, DC, invoice, courier, AWB, welcome label, delivery and feedback all run here.</p>
    </section>
  );
}
